// src/services/tokenUsageTracker.ts
// ============================================
// Token 使用追踪服务（统一三套 LLM 调用路径）
//
// 用户需求：针对消耗 token 的用户，记录后台查看次数和消耗 token 的记录
//
// 职责：
//   1. 统一捕获 LLMRouter / HermesRuntime / services/deepseek.ts 三条调用路径的 token 消耗
//   2. 按用户归因（username + role），记录每次调用
//   3. 持久化到 Vault：XEAOS/Audit/TokenLog-{YYYY-MM-DD}.md（按天分文件，追加写入）
//   4. 提供查询接口：按用户聚合统计、按时间查询明细
//   5. 内存缓存当日数据，避免每次查询都读 Vault
//
// Vault 文件格式（Markdown 表格）：
//   ---
//   type: token_log
//   date: 2026-08-02
//   module: llm
//   ---
//   # Token 消耗日志 2026-08-02
//
//   | 时间 | 用户 | 角色 | Provider | 任务 | 模型 | 输入Token | 输出Token | 总Token | 耗时ms | 成功 |
//   |------|------|------|----------|------|------|-----------|-----------|---------|--------|------|
//   | 09:30:15 | xiaoshe | super_admin | deepseek | chat | deepseek-chat | 120 | 350 | 470 | 1200 | ✅ |
// ============================================

import { useUserStore } from "../stores/userStore";

// ============================================
// 类型定义
// ============================================

export interface TokenUsageRecord {
  /** ISO 时间戳 */
  timestamp: string;
  /** 用户名（未登录时为 anonymous） */
  username: string;
  /** 用户角色 */
  role: string;
  /** LLM Provider 名称（deepseek / glm / openai / claude / qwen / gemini） */
  provider: string;
  /** 任务类型（chat / embed / complete） */
  task: string;
  /** 模型名称 */
  model: string;
  /** 输入 token 数 */
  promptTokens: number;
  /** 输出 token 数 */
  completionTokens: number;
  /** 总 token 数 */
  totalTokens: number;
  /** 调用耗时（毫秒） */
  latency: number;
  /** 是否成功 */
  success: boolean;
  /** 失败时的错误信息 */
  error?: string;
}

export interface UserTokenStats {
  username: string;
  role: string;
  /** 总调用次数 */
  callCount: number;
  /** 成功调用次数 */
  successCount: number;
  /** 失败调用次数 */
  failedCount: number;
  /** 总输入 token */
  totalPromptTokens: number;
  /** 总输出 token */
  totalCompletionTokens: number;
  /** 总 token 消耗 */
  totalTokens: number;
  /** 最后一次调用时间 */
  lastCallAt: string;
}

export interface TokenStats {
  /** 总调用次数 */
  totalCalls: number;
  /** 总 token 消耗 */
  totalTokens: number;
  /** 总输入 token */
  totalPromptTokens: number;
  /** 总输出 token */
  totalCompletionTokens: number;
  /** 按用户聚合的统计 */
  byUser: UserTokenStats[];
}

// ============================================
// TokenUsageTracker 单例
// ============================================

class TokenUsageTrackerClass {
  /** 当日内存缓存（避免频繁读 Vault） */
  private dailyCache: TokenUsageRecord[] = [];
  /** 当日日期（YYYY-MM-DD），用于跨日清空缓存 */
  private cacheDate: string = new Date().toISOString().slice(0, 10);
  /** 写入去抖：避免高频写入 Vault（每 5 秒批量写一次） */
  private pendingWrites: TokenUsageRecord[] = [];
  private writeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly WRITE_DEBOUNCE_MS = 5000;
  private readonly MAX_BATCH_SIZE = 50;

  /**
   * 记录一次 token 使用
   * 同时写入内存缓存和异步持久化到 Vault
   */
  recordUsage(record: Omit<TokenUsageRecord, "username" | "role"> & Partial<Pick<TokenUsageRecord, "username" | "role">>): void {
    // 检查跨日清空
    const today = new Date().toISOString().slice(0, 10);
    if (today !== this.cacheDate) {
      this.dailyCache = [];
      this.cacheDate = today;
    }

    // 从 userStore 获取当前用户信息（未登录时为 anonymous）
    const userState = useUserStore.getState();
    const username = record.username || userState.currentUser?.username || "anonymous";
    const role = record.role || userState.currentUser?.role || "unknown";

    const fullRecord: TokenUsageRecord = {
      ...record,
      username,
      role,
    };

    // 写入内存缓存（最多保留 2000 条）
    this.dailyCache.push(fullRecord);
    if (this.dailyCache.length > 2000) {
      this.dailyCache.shift();
    }

    // 加入待写入队列（去抖批量写）
    this.pendingWrites.push(fullRecord);

    // 达到批次上限立即写入
    if (this.pendingWrites.length >= this.MAX_BATCH_SIZE) {
      void this.flushWrites();
    } else {
      this.scheduleFlush();
    }
  }

  /**
   * 调度去抖写入
   */
  private scheduleFlush(): void {
    if (this.writeTimer) return;
    this.writeTimer = setTimeout(() => {
      this.writeTimer = null;
      void this.flushWrites();
    }, this.WRITE_DEBOUNCE_MS);
  }

  /**
   * 批量写入 Vault
   * 采用「读取现有内容 → 追加新行 → 写回」模式
   */
  private async flushWrites(): Promise<void> {
    if (this.pendingWrites.length === 0) return;

    // 取出待写入记录
    const toWrite = this.pendingWrites.splice(0);
    if (this.writeTimer) {
      clearTimeout(this.writeTimer);
      this.writeTimer = null;
    }

    try {
      const today = new Date().toISOString().slice(0, 10);
      const relPath = `XEAOS/Audit/TokenLog-${today}.md`;

      // 读取现有内容
      let existingContent = "";
      try {
        const readResp = await fetch(`/vault-api/read?path=${encodeURIComponent(relPath)}`);
        if (readResp.ok) {
          const readData = await readResp.json();
          if (readData?.success && typeof readData.content === "string") {
            existingContent = readData.content;
          }
        }
      } catch {
        // 文件不存在，正常情况
      }

      // 构建新行
      const newRows = toWrite.map((r) => this.formatTableRow(r)).join("\n");

      let newContent: string;
      if (existingContent) {
        // 追加到表格末尾（找最后一行 | 开头的位置）
        const lines = existingContent.split("\n");
        let lastTableRowIdx = -1;
        for (let i = lines.length - 1; i >= 0; i--) {
          if (lines[i].trim().startsWith("|")) {
            lastTableRowIdx = i;
            break;
          }
        }
        if (lastTableRowIdx >= 0) {
          lines.splice(lastTableRowIdx + 1, 0, newRows);
          newContent = lines.join("\n");
        } else {
          // 表格不存在，追加新表格
          newContent = existingContent + "\n\n" + this.buildTableHeader(today) + "\n" + newRows + "\n";
        }
      } else {
        // 新建文件
        newContent = this.buildTableHeader(today) + "\n" + newRows + "\n";
      }

      // 写回 Vault
      await fetch(`/vault-api/write?path=${encodeURIComponent(relPath)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: newContent }),
      });
    } catch (err) {
      // token 日志写入失败不阻塞主流程
      console.warn("[TokenUsageTracker] Vault 写入失败:", err);
      // 失败的记录放回队列前端，下次重试
      this.pendingWrites.unshift(...toWrite);
      this.scheduleFlush();
    }
  }

  /**
   * 构建表格头
   */
  private buildTableHeader(date: string): string {
    return [
      "---",
      "type: token_log",
      `date: ${date}`,
      "module: llm",
      "---",
      "",
      `# Token 消耗日志 ${date}`,
      "",
      "白皮书铁律一：记录所有 LLM 调用的 token 消耗，按用户归因。",
      "",
      "| 时间 | 用户 | 角色 | Provider | 任务 | 模型 | 输入Token | 输出Token | 总Token | 耗时ms | 成功 |",
      "|------|------|------|----------|------|------|-----------|-----------|---------|--------|------|",
    ].join("\n");
  }

  /**
   * 格式化一条记录为表格行
   */
  private formatTableRow(r: TokenUsageRecord): string {
    const time = r.timestamp.slice(11, 19); // HH:MM:SS
    const successMark = r.success ? "✅" : "❌";
    const errorStr = r.error ? ` · ${r.error.slice(0, 50)}` : "";
    return `| ${time} | ${r.username} | ${r.role} | ${r.provider} | ${r.task} | ${r.model} | ${r.promptTokens} | ${r.completionTokens} | ${r.totalTokens} | ${r.latency} | ${successMark}${errorStr} |`;
  }

  /**
   * 获取当日统计（从内存缓存）
   */
  getTodayStats(): TokenStats {
    const userMap = new Map<string, UserTokenStats>();

    for (const r of this.dailyCache) {
      const key = r.username;
      if (!userMap.has(key)) {
        userMap.set(key, {
          username: r.username,
          role: r.role,
          callCount: 0,
          successCount: 0,
          failedCount: 0,
          totalPromptTokens: 0,
          totalCompletionTokens: 0,
          totalTokens: 0,
          lastCallAt: r.timestamp,
        });
      }
      const stats = userMap.get(key)!;
      stats.callCount++;
      if (r.success) stats.successCount++;
      else stats.failedCount++;
      stats.totalPromptTokens += r.promptTokens;
      stats.totalCompletionTokens += r.completionTokens;
      stats.totalTokens += r.totalTokens;
      if (r.timestamp > stats.lastCallAt) {
        stats.lastCallAt = r.timestamp;
      }
    }

    const byUser = Array.from(userMap.values()).sort((a, b) => b.totalTokens - a.totalTokens);

    return {
      totalCalls: this.dailyCache.length,
      totalTokens: byUser.reduce((sum, u) => sum + u.totalTokens, 0),
      totalPromptTokens: byUser.reduce((sum, u) => sum + u.totalPromptTokens, 0),
      totalCompletionTokens: byUser.reduce((sum, u) => sum + u.totalCompletionTokens, 0),
      byUser,
    };
  }

  /**
   * 获取当日明细（从内存缓存）
   * @param limit 最多返回条数，默认 100
   * @param usernameFilter 可选：按用户名过滤
   */
  getTodayRecords(limit: number = 100, usernameFilter?: string): TokenUsageRecord[] {
    let records = this.dailyCache;
    if (usernameFilter) {
      records = records.filter((r) => r.username === usernameFilter);
    }
    // 倒序（最新在前）
    return records.slice(-limit).reverse();
  }

  /**
   * 从 Vault 查询指定日期的 Token 日志
   * @param date YYYY-MM-DD，默认今天
   */
  async queryDailyLog(date?: string): Promise<{ success: boolean; content: string; records: TokenUsageRecord[] }> {
    try {
      const targetDate = date || new Date().toISOString().slice(0, 10);
      const relPath = `XEAOS/Audit/TokenLog-${targetDate}.md`;
      const readResp = await fetch(`/vault-api/read?path=${encodeURIComponent(relPath)}`);
      if (!readResp.ok) {
        return { success: false, content: "", records: [] };
      }
      const readData = await readResp.json();
      if (!readData?.success || typeof readData.content !== "string") {
        return { success: false, content: "", records: [] };
      }

      // 解析表格行为 TokenUsageRecord
      const records: TokenUsageRecord[] = [];
      const lines = readData.content.split("\n");
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith("|") || trimmed.includes("---") || trimmed.includes("时间")) continue;
        const cells = trimmed.split("|").map((c: string) => c.trim()).filter((x: string) => x.length > 0);
        if (cells.length < 11) continue;
        // | 时间 | 用户 | 角色 | Provider | 任务 | 模型 | 输入Token | 输出Token | 总Token | 耗时ms | 成功 |
        const [time, username, role, provider, task, model, promptTokens, completionTokens, totalTokens, latency, successStr] = cells;
        const success = successStr.includes("✅");
        const errorMatch = successStr.match(/·\s*(.+)$/);
        records.push({
          timestamp: `${targetDate}T${time}`,
          username,
          role,
          provider,
          task,
          model,
          promptTokens: Number(promptTokens) || 0,
          completionTokens: Number(completionTokens) || 0,
          totalTokens: Number(totalTokens) || 0,
          latency: Number(latency) || 0,
          success,
          error: errorMatch ? errorMatch[1].trim() : undefined,
        });
      }
      return { success: true, content: readData.content, records };
    } catch (err) {
      console.warn("[TokenUsageTracker] 查询 Vault 日志失败:", err);
      return { success: false, content: "", records: [] };
    }
  }

  /**
   * 强制刷新（立即写入待处理记录）
   */
  async flush(): Promise<void> {
    await this.flushWrites();
  }
}

// 导出单例
export const tokenUsageTracker = new TokenUsageTrackerClass();
export default tokenUsageTracker;
